import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { MenuModule } from 'primeng/menu';
import { environment } from '../../../../../environments/environment';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { translateSignal, TranslocoModule } from '@jsverse/transloco';
import { RouterLinkFactory } from '../../../../core/router/router-link-factory';
import { IconsService } from '@rieaya-core';
import { AuthService } from '../../../../core/services/auth.service';
import { UserService } from '../../../../core/services/user.service';
import { BaseResponse } from '../../../../core/interfaces/base-response';
import { User } from '../../../../core/interfaces/user';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  LANG,
  PERMISSIONS,
  TOKEN,
  USER_DATA,
} from '../../../../core/utils/constant';

@Component({
  selector: 'app-user-avatar',
  imports: [
    AvatarModule,
    MenuModule,
    RouterModule,
    CommonModule,
    ConfirmDialogModule,
    TranslocoModule,
  ],
  templateUrl: './user-avatar.component.html',
  styleUrl: './user-avatar.component.css',
  providers: [ConfirmationService],
})
export class UserAvatarComponent {
  routerLinks = inject(RouterLinkFactory);
  icons = inject(IconsService);
  private confirmationService = inject(ConfirmationService);
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private destroyRef = inject(DestroyRef);

  items: MenuItem[] = [];

  constructor() {
    const translations = {
      profile: translateSignal('shared/profile'),
      logout: translateSignal('shared/logout'),
      confirm: translateSignal('shared/confirm'),
      confirmMessage: translateSignal('shared/confirmMessage'),
      accept: translateSignal('shared/yes'),
      reject: translateSignal('shared/no'),
    };

    effect(() => {
      this.items = [
        {
          label: translations.profile(),
          icon: this.icons.users,
          route: this.routerLinks.profile()[0].split('/')[1],
        },
        {
          label: translations.logout(),
          icon: this.icons.signOut,
          command: () => {
            this.confirmationService.confirm({
              key: 'cofirm-logout',
              message: translations.confirmMessage(),
              header: translations.confirm(),
              icon: this.icons.exclamationTriangle,
              accept: () => {
                this.logout();
              },
              acceptButtonStyleClass:
                '!bg-red-500 !hover:bg-red-600 !border-red-500',
              acceptLabel: translations.accept(),
              rejectLabel: translations.reject(),
            });
          },
        },
      ];
    });

    const userData = JSON.parse(localStorage.getItem(USER_DATA) || '');

    if (userData?.email) {
      this.userData.set(userData as any);
    } else {
      this.userService
        .getCurrentUser()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response: BaseResponse<User>) => {
            const role = JSON.parse(
              localStorage.getItem(USER_DATA) || ''
            )?.role;
            const user = { ...response.data, role };
            this.userData.set(user as any);
          },
        });
    }
  }

  userData = signal<any>(JSON.parse(localStorage.getItem(USER_DATA) || ''));
  username = computed(() => this.userData().name);
  email = computed(() => this.userData().email);
  baseUrl = environment.base_url;

  logout() {
    this.authService
      .logout()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          localStorage.removeItem(TOKEN);
          localStorage.removeItem(USER_DATA);
          localStorage.removeItem(LANG);
          localStorage.removeItem(PERMISSIONS);
          window.location.href = environment.LoginSSOROP;
        },
      });
  }
}
